import { Component, OnInit, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatCardModule } from '@angular/material/card';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTabsModule } from '@angular/material/tabs';
import { MatDialog } from '@angular/material/dialog';
import {
  BarrierDto, CameraDto, CreateBarrierRequest, CreateCameraRequest, CreateDetectionPointRequest,
  CreateDetectionSequencePatternRequest, DetectionPointDto, DetectionSequencePatternDto, ListBarriersRequest,
  ListDetectionPointsRequest, ListDetectionSequencePatternsRequest, DeviceDto, DeviceGroupDto,
  DetectionPointDtoPaginationResult, DetectionSequencePatternDtoPaginationResult, ParkingSpaceDto,
  ParkingSpaceNoZonesDto, UpdateCameraRequest, BarrierDtoPaginationResult, UpdateBarrierRequest,
  UpdateDeviceGroupRequest, DisplayDto, DisplayDtoPaginationResult, CreateDisplayRequest, UpdateDisplayRequest,
  CreatePaymentTerminalRequest, PaymentTerminalDto, PaymentTerminalDtoPaginationResult, ListPaymentTerminalsRequest,
  UpdatePaymentTerminalRequest, PointDirection
} from '../../../../api/gen';
import { InfrastructureService } from '../../../../api/gen/api/infrastructure.service';
import {
  AssignExistingDeviceDialogComponent,
  AssignExistingDeviceDialogData
} from './assign-existing-device-dialog/assign-existing-device-dialog.component';
import { DeviceGroupService } from '../../../../api/gen/api/deviceGroup.service';
import { BarrierService } from '../../../../api/gen/api/barrier.service';
import { CameraService } from '../../../../api/gen/api/camera.service';
import { DetectionPointService } from '../../../../api/gen/api/detectionPoint.service';
import { DetectionSequencePatternService } from '../../../../api/gen/api/detectionSequencePattern.service';
import { ParkingZoneService } from '../../../../api/gen/api/parkingZone.service';
import { DisplayService } from '../../../../api/gen/api/display.service';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { ZoneCameraComponent } from './zone-camera/zone-camera.component';
import { DetectionPointChange, ZoneDetectionPointComponent } from './zone-detection-point/zone-detection-point.component';
import { ZoneDetectionPatternComponent } from './zone-detection-pattern/zone-detection-pattern.component';
import { ZoneListDevicesComponent } from './zone-list-devices/zone-list-devices.component';
import { CreateCameraFormComponent } from './create-camera-form/create-camera-form.component';
import { CreateDetectionPointFormComponent } from './create-detection-point-form/create-detection-point-form.component';
import { CreatePatternFormComponent } from './create-pattern-form/create-pattern-form.component';
import { MatDividerModule } from '@angular/material/divider';
import { MatDialogModule } from '@angular/material/dialog';
import { ZoneBarrierComponent } from './zone-barrier/zone-barrier.component';
import { CreateBarrierFormComponent } from './create-barrier-form/create-barrier-form.component';
import { ZoneDisplayComponent } from './zone-display/zone-display.component';
import { CreateDisplayFormComponent } from './create-display-form/create-display-form.component';
import { PaymentTerminalService } from '../../../../api/gen/api/paymentTerminal.service';
import { CreatePaymentTerminalFormComponent } from './create-payment-terminal-form/create-payment-terminal-form.component';
import { ZonePaymentTerminalComponent } from './zone-payment-terminal/zone-payment-terminal.component';

export interface DeviceGroupItemZoneConfigData {
  deviceGroup: DeviceGroupDto;
}

/**
 * The zone's points feed the pattern form's step picker, which needs points from the lane's other device
 * group too; ask for all of them in one request instead of the API's default page of 20.
 */
const ZONE_DETECTION_POINTS_PAGE_SIZE = 200;

@Component({
  selector: 'app-device-group-item-zone-config',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatListModule,
    MatProgressBarModule,
    MatSnackBarModule,
    MatCardModule,
    MatToolbarModule,
    MatTabsModule,
    MatDividerModule,
    MatDialogModule,
    ZoneCameraComponent,
    ZoneBarrierComponent,
    ZoneDisplayComponent,
    ZoneDetectionPointComponent,
    ZoneDetectionPatternComponent,
    ZoneListDevicesComponent,
    CreateCameraFormComponent,
    CreateBarrierFormComponent,
    CreateDisplayFormComponent,
    CreatePaymentTerminalFormComponent,
    ZonePaymentTerminalComponent,
    CreateDetectionPointFormComponent,
    CreatePatternFormComponent
  ],
  templateUrl: './device-group-item-zone-config.component.html',
  styleUrls: ['./device-group-item-zone-config.component.scss']
})
export class DeviceGroupItemZoneConfigComponent implements OnInit {
  deviceGroup = signal<DeviceGroupDto | null>(null);
  cameras = signal<CameraDto[]>([]);
  barrier = signal<BarrierDto | null>(null);
  devices = signal<DeviceDto[]>([]);
  detectionPoints = signal<DetectionPointDto[]>([]);
  /** The Points tab shows only this group's points: those whose camera is in the group. */
  groupDetectionPoints = computed(() => {
    const cameraIds = new Set(this.cameras().map(c => c.id).filter((id): id is string => !!id));
    return this.detectionPoints().filter(p => {
      const cameraId = p.cameraId ?? p.camera?.id;
      return !!cameraId && cameraIds.has(cameraId);
    });
  });
  detectionPatterns = signal<DetectionSequencePatternDto[]>([]);
  isLoading = signal(true);
  isProcessing = signal(false);
  connectingCameras = signal<Set<string>>(new Set());
  startingLPRCameras = signal<Set<string>>(new Set());
  selectedTabIndex = signal(0);
  highlightedCameraId = signal<string | null>(null);
  parkingSpaceId = signal<string | null>(null);
  parkingSpace = signal<ParkingSpaceNoZonesDto | null>(null);
  showCreateForm = signal(false);
  editingCamera = signal<CameraDto | null>(null);
  showCreateBarrierForm = signal(false);
  editingBarrier = signal<BarrierDto | null>(null);
  displays = signal<DisplayDto[]>([]);
  showCreateDisplayForm = signal(false);
  editingDisplay = signal<DisplayDto | null>(null);
  paymentTerminals = signal<PaymentTerminalDto[]>([]);
  showCreatePaymentTerminalForm = signal(false);
  /** Non-null while editing that terminal; the same form component serves both modes. */
  editingPaymentTerminal = signal<PaymentTerminalDto | null>(null);
  showCreateDetectionPointForm = signal(false);
  showCreatePatternForm = signal(false);
  zoneId = signal<string>('');
  infrastructureId = signal<string | null>(null);
  isEditingName = signal(false);
  editingGroupName = '';

  /**
   * Which reads this group serves. Only matters for a multi-direction camera that sits in several groups
   * (an entry group and an exit group); "Both" - or never having set it - means every read, which is
   * what every group did before this existed.
   */
  readonly directionOptions: { value: PointDirection; label: string }[] = [
    { value: PointDirection.Both, label: 'Both' },
    { value: PointDirection.Approach, label: 'Approach' },
    { value: PointDirection.Departure, label: 'Departure' }
  ];
  /** A group with no direction behaves as Both, so that is what the select shows. */
  currentDirection = computed<PointDirection>(() => this.deviceGroup()?.direction ?? PointDirection.Both);

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private deviceGroupService: DeviceGroupService,
    private cameraService: CameraService,
    private barrierService: BarrierService,
    private displayService: DisplayService,
    private detectionPointService: DetectionPointService,
    private detectionSequencePatternService: DetectionSequencePatternService,
    private parkingZoneService: ParkingZoneService,
    private infrastructureService: InfrastructureService,
    private paymentTerminalService: PaymentTerminalService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar
  ) { }

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      const deviceGroupId = params['deviceGroupId'];
      const zoneId = params['zoneId'];
      this.zoneId.set(zoneId);
      if (deviceGroupId && zoneId) {
        this.loadDeviceGroupData(deviceGroupId, zoneId);
      }
    });
  }

  async loadDeviceGroupData(deviceGroupId: string, zoneId: string): Promise<void> {
    this.isLoading.set(true);
    try {
      // Load parking zone to get parking space ID
      try {
        const zone = await firstValueFrom(
          this.parkingZoneService.parkingZoneParkingZoneIdGet(zoneId)
        );
        this.parkingSpaceId.set(zone.parkingSpaceId || null);
        this.parkingSpace.set(zone.parkingSpaces?.at(0) || null);
        this.infrastructureId.set(zone.zoneInfrastructureId || null);
      } catch (error) {
        console.warn('Could not load zone information:', error);
      }

      // Load device group details including its cameras and devices
      const deviceGroupDetails = await firstValueFrom(
        this.deviceGroupService.deviceGroupsDeviceGroupIdGet(deviceGroupId, false, true)
      ) as DeviceGroupDto;

      this.deviceGroup.set(deviceGroupDetails);
      this.cameras.set(deviceGroupDetails.cameras || []);
      this.devices.set(deviceGroupDetails.allDevices || []);

      await this.loadBarrierForGroup(deviceGroupId);

      // Load detection points for this zone
      try {
        const pointsReq = await firstValueFrom(
          this.detectionPointService.detectionPointsFilterPost({
            targetZoneId: zoneId,
            pageSize: ZONE_DETECTION_POINTS_PAGE_SIZE
          } as ListDetectionPointsRequest)
        );
        this.detectionPoints.set((pointsReq as DetectionPointDtoPaginationResult)?.items || []);
      } catch (e) {
        console.warn('Could not load detection points:', e);
      }

      // Load detection patterns for this zone
      try {
        const patternsReq = await firstValueFrom(
          this.detectionSequencePatternService.detectionSequencePatternsFilterPost({
            targetZoneId: zoneId
          } as ListDetectionSequencePatternsRequest)
        );
        this.detectionPatterns.set((patternsReq as DetectionSequencePatternDtoPaginationResult)?.items || []);
      } catch (e) {
        console.warn('Could not load detection patterns:', e);
      }

      // Load displays for this device group
      await this.loadDisplaysForGroup(deviceGroupId);

      // Load payment terminals for this device group
      await this.loadPaymentTerminalsForGroup(deviceGroupId);
    } catch (error) {
      console.error('Error loading device group data:', error);
      this.snackBar.open('Failed to load device group.', 'Close', { duration: 3000 });
    } finally {
      this.isLoading.set(false);
    }
  }

  private async loadDisplaysForGroup(deviceGroupId: string): Promise<void> {
    try {
      const result = await firstValueFrom(
        this.displayService.displaysGet(undefined, undefined, deviceGroupId, undefined, undefined, 1, 100)
      ) as DisplayDtoPaginationResult;
      const allDisplays = result?.items || [];
      // Filter client-side until a deviceGroupId filter is available in the API (see BE task)
      this.displays.set(allDisplays);
    } catch (error) {
      console.warn('Could not load displays:', error);
      this.displays.set([]);
    }
  }

  private async loadPaymentTerminalsForGroup(deviceGroupId: string): Promise<void> {
    try {
      const result = await firstValueFrom(
        this.paymentTerminalService.paymentTerminalsFilterPost({
          deviceGroupId: deviceGroupId,
          pageNumber: 1,
          pageSize: 100
        } as ListPaymentTerminalsRequest)
      ) as PaymentTerminalDtoPaginationResult;
      this.paymentTerminals.set(result?.items || []);
    } catch (error) {
      console.warn('Could not load payment terminals:', error);
      this.paymentTerminals.set([]);
    }
  }

  openCreatePaymentTerminalForm(): void {
    this.showCreatePaymentTerminalForm.set(true);
  }

  openEditPaymentTerminalForm(terminal: PaymentTerminalDto): void {
    this.editingPaymentTerminal.set(terminal);
    this.showCreatePaymentTerminalForm.set(true);
  }

  async onUpdatePaymentTerminal(payload: { id: string; request: UpdatePaymentTerminalRequest }): Promise<void> {
    try {
      this.isProcessing.set(true);
      const updated = await firstValueFrom(
        this.paymentTerminalService.paymentTerminalsTerminalIdPut(payload.id, payload.request)
      ) as PaymentTerminalDto;
      // Replace in place rather than reloading: the card shows the new reader endpoint immediately,
      // which is the whole point of the edit.
      this.paymentTerminals.update(list => list.map(t => (t.id === payload.id ? updated : t)));
      this.closePaymentTerminalForm();
    } catch (error) {
      console.error('Error updating payment terminal', error);
    } finally {
      this.isProcessing.set(false);
    }
  }

  closePaymentTerminalForm(): void {
    this.showCreatePaymentTerminalForm.set(false);
    this.editingPaymentTerminal.set(null);
  }

  async onCreatePaymentTerminal(formData: CreatePaymentTerminalRequest): Promise<void> {
    this.isProcessing.set(true);
    try {
      const deviceGroupId = this.deviceGroup()?.id;
      if (!deviceGroupId) throw new Error('Device group ID is required');

      const createRequest: CreatePaymentTerminalRequest = { ...formData, deviceGroupId };
      const created = await firstValueFrom(
        this.paymentTerminalService.paymentTerminalsPost(createRequest)
      ) as PaymentTerminalDto;

      this.paymentTerminals.update(list => [...list, created]);
      this.closePaymentTerminalForm();
      this.snackBar.open(`Payment terminal "${created.deviceName}" created successfully`, 'Close', { duration: 2500 });
    } catch (error: any) {
      console.error('Error creating payment terminal:', error);
      // Surface the backend validation / uniqueness message (e.g. "Provider Terminal Id Already Registered").
      const body = error?.error;
      const detail = (typeof body === 'string' ? body : (body?.error?.detail ?? body?.detail ?? body?.title))
        || 'Failed to create payment terminal';
      this.snackBar.open(detail, 'Close', { duration: 5000 });
    } finally {
      this.isProcessing.set(false);
    }
  }

  async deletePaymentTerminal(terminalId: string): Promise<void> {
    this.isProcessing.set(true);
    try {
      await firstValueFrom(this.paymentTerminalService.paymentTerminalsTerminalIdDelete(terminalId));
      this.paymentTerminals.update(list => list.filter(t => t.id !== terminalId));
      this.snackBar.open('Payment terminal removed successfully', 'Close', { duration: 2000 });
    } catch (error) {
      console.error('Error deleting payment terminal:', error);
      this.snackBar.open('Failed to delete payment terminal', 'Close', { duration: 3000 });
    } finally {
      this.isProcessing.set(false);
    }
  }

  private async loadBarrierForGroup(deviceGroupId: string): Promise<void> {
    try {
      const barriersReq = await firstValueFrom(
        this.barrierService.barriersFilterPost({
          deviceGroupId: deviceGroupId,
          pageNumber: 1,
          pageSize: 1
        } as ListBarriersRequest)
      );
      const items = (barriersReq as BarrierDtoPaginationResult)?.items || [];
      this.barrier.set(items[0] || null);
    } catch (error) {
      console.warn('Could not load barriers:', error);
      this.barrier.set(null);
    }
  }

  async onCreateBarrier(formData: CreateBarrierRequest): Promise<void> {
    this.isProcessing.set(true);
    try {
      const deviceGroupId = this.deviceGroup()?.id;
      if (!deviceGroupId) {
        throw new Error('Device group ID is required');
      }

      const editingBarrier = this.editingBarrier();
      if (editingBarrier?.id) {
        // Update existing barrier
        const updateRequest = {
          ...formData,
          deviceGroupId: deviceGroupId
        };

        const updatedBarrier = await firstValueFrom(
          this.barrierService.barriersBarrierIdPut(editingBarrier.id, updateRequest)
        ) as BarrierDto;

        this.barrier.set(updatedBarrier);
        this.showCreateBarrierForm.set(false);
        this.editingBarrier.set(null);
        const barrierName = formData.deviceName || editingBarrier.deviceName || 'Barrier';
        this.snackBar.open(`Barrier "${barrierName}" updated successfully`, 'Close', { duration: 2000 });
      } else {
        // Create new barrier
        if (this.barrier()) {
          this.snackBar.open('This device group already has a barrier.', 'Close', { duration: 3000 });
          return;
        }

        const createRequest: CreateBarrierRequest = {
          ...formData,
          deviceGroupId: deviceGroupId
        };

        const newBarrier = await firstValueFrom(
          this.barrierService.barriersPost(createRequest)
        ) as BarrierDto;

        this.barrier.set(newBarrier);
        this.showCreateBarrierForm.set(false);
        this.snackBar.open(`Barrier "${formData.deviceName}" created successfully`, 'Close', { duration: 2000 });
      }
    } catch (error) {
      console.error('Error saving barrier:', error);
      this.snackBar.open('Failed to save barrier', 'Close', { duration: 3000 });
    } finally {
      this.isProcessing.set(false);
    }
  }

  async deleteBarrier(barrierId: string): Promise<void> {
    this.isProcessing.set(true);
    try {
      await firstValueFrom(
        this.barrierService.barriersBarrierIdDelete(barrierId)
      );
      this.barrier.set(null);
      this.snackBar.open('Barrier removed successfully', 'Close', { duration: 2000 });
    } catch (error) {
      console.error('Error deleting barrier:', error);
      this.snackBar.open('Failed to delete barrier', 'Close', { duration: 3000 });
    } finally {
      this.isProcessing.set(false);
    }
  }

  openCreateDisplayForm(): void {
    this.editingDisplay.set(null);
    this.showCreateDisplayForm.set(true);
  }

  openEditDisplayForm(display: DisplayDto): void {
    this.editingDisplay.set(display);
    this.showCreateDisplayForm.set(true);
  }

  closeDisplayForm(): void {
    this.showCreateDisplayForm.set(false);
    this.editingDisplay.set(null);
  }

  async onSaveDisplay(formData: CreateDisplayRequest): Promise<void> {
    this.isProcessing.set(true);
    try {
      const deviceGroupId = this.deviceGroup()?.id;
      if (!deviceGroupId) throw new Error('Device group ID is required');

      const editingDisplay = this.editingDisplay();
      if (editingDisplay?.id) {
        const updateRequest: UpdateDisplayRequest = {
          deviceName: formData.deviceName,
          deviceDescription: formData.deviceDescription,
          macAddress: formData.macAddress,
          piDeviceId: formData.piDeviceId
        };
        const updated = await firstValueFrom(
          this.displayService.displaysDisplayIdPut(editingDisplay.id, updateRequest)
        ) as DisplayDto;
        this.displays.update(list => list.map(d => d.id === updated.id ? updated : d));
        this.snackBar.open(`Display "${updated.deviceName}" updated successfully`, 'Close', { duration: 2000 });
      } else {
        const createRequest: CreateDisplayRequest = { ...formData, deviceGroupId };
        const created = await firstValueFrom(
          this.displayService.displaysPost(createRequest)
        ) as DisplayDto;
        this.displays.update(list => [...list, created]);
        this.snackBar.open(`Display "${created.deviceName}" created successfully`, 'Close', { duration: 2000 });
      }
      this.closeDisplayForm();
    } catch (error) {
      console.error('Error saving display:', error);
      this.snackBar.open('Failed to save display', 'Close', { duration: 3000 });
    } finally {
      this.isProcessing.set(false);
    }
  }

  async deleteDisplay(displayId: string): Promise<void> {
    this.isProcessing.set(true);
    try {
      await firstValueFrom(this.displayService.displaysDisplayIdDelete(displayId));
      this.displays.update(list => list.filter(d => d.id !== displayId));
      this.snackBar.open('Display removed successfully', 'Close', { duration: 2000 });
    } catch (error) {
      console.error('Error deleting display:', error);
      this.snackBar.open('Failed to delete display', 'Close', { duration: 3000 });
    } finally {
      this.isProcessing.set(false);
    }
  }

  async onTestDisplayHeartbeat(displayId: string): Promise<void> {
    this.isProcessing.set(true);
    try {
      await firstValueFrom(this.displayService.displaysDisplayIdHeartbeatPost(displayId));
      // Refresh display to show updated lastHeartbeatAt
      const updated = await firstValueFrom(this.displayService.displaysDisplayIdGet(displayId));
      this.displays.update(list => list.map(d => d.id === updated.id ? updated : d));
      this.snackBar.open('Heartbeat sent successfully', 'Close', { duration: 2000 });
    } catch (error) {
      console.error('Error sending heartbeat:', error);
      this.snackBar.open('Failed to send heartbeat', 'Close', { duration: 3000 });
    } finally {
      this.isProcessing.set(false);
    }
  }

  async onCreateCamera(formData: CreateCameraRequest): Promise<void> {
    this.isProcessing.set(true);
    try {
      const deviceGroupId = this.deviceGroup()?.id;
      if (!deviceGroupId) {
        throw new Error('Device group ID is required');
      }

      const editingCamera = this.editingCamera();
      if (editingCamera?.id) {
        const updateRequest: UpdateCameraRequest = {
          ...formData,
        };

        await firstValueFrom(
          this.cameraService.camerasCameraIdPut(editingCamera.id, updateRequest)
        );

        const updatedGroup = await firstValueFrom(
          this.deviceGroupService.deviceGroupsDeviceGroupIdGet(deviceGroupId)
        ) as DeviceGroupDto;
        this.cameras.set(updatedGroup.cameras || []);
        this.devices.set(updatedGroup.allDevices || []);

        const cameraName = formData.deviceName || editingCamera.deviceName || 'Camera';
        this.snackBar.open(`Camera "${cameraName}" updated successfully`, 'Close', { duration: 2000 });
        this.closeCameraForm();
      } else {
        const createRequest: CreateCameraRequest = {
          ...formData,
          deviceGroupId: deviceGroupId
        };

        // Create camera
        await firstValueFrom(
          this.cameraService.camerasPost(createRequest)
        ) as CameraDto;

        // Reload cameras
        const updatedGroup = await firstValueFrom(
          this.deviceGroupService.deviceGroupsDeviceGroupIdGet(deviceGroupId)
        ) as DeviceGroupDto;
        this.cameras.set(updatedGroup.cameras || []);
        this.devices.set(updatedGroup.allDevices || []);

        this.snackBar.open(`Camera "${formData.deviceName}" created successfully`, 'Close', { duration: 2000 });
        this.closeCameraForm();
      }
    } catch (error) {
      console.error('Error creating camera:', error);
      this.snackBar.open('Failed to create camera', 'Close', { duration: 3000 });
    } finally {
      this.isProcessing.set(false);
    }
  }

  async onCreateDetectionPoint(formData: any): Promise<void> {
    this.isProcessing.set(true);
    try {
      const zoneId = this.zoneId();
      if (!zoneId) {
        throw new Error('Zone ID is required');
      }

      const createRequest: CreateDetectionPointRequest = {
        ...formData
      };

      // Create detection point
      const newPoint = await firstValueFrom(
        this.detectionPointService.detectionPointsPost(createRequest)
      ) as DetectionPointDto;

      // Reload detection points
      const pointsReq = await firstValueFrom(
        this.detectionPointService.detectionPointsFilterPost({
          targetZoneId: zoneId,
          pageSize: ZONE_DETECTION_POINTS_PAGE_SIZE
        } as ListDetectionPointsRequest)
      );
      this.detectionPoints.set((pointsReq as DetectionPointDtoPaginationResult)?.items || []);

      this.snackBar.open(`Detection Point "${formData.name}" created successfully`, 'Close', { duration: 2000 });
      this.showCreateDetectionPointForm.set(false);
    } catch (error) {
      console.error('Error creating detection point:', error);
      this.snackBar.open('Failed to create detection point', 'Close', { duration: 3000 });
    } finally {
      this.isProcessing.set(false);
    }
  }

  async removeCamera(cameraId: string): Promise<void> {
    this.isProcessing.set(true);
    try {
      const deviceGroupId = this.deviceGroup()?.id;
      if (!deviceGroupId) {
        throw new Error('Device group ID is required');
      }

      // Get the camera name before removal for the message
      const cameraToRemove = this.cameras().find(c => c.id === cameraId);
      const cameraName = cameraToRemove?.deviceName || 'Camera';

      // Remove camera from device group
      await firstValueFrom(
        this.deviceGroupService.deviceGroupsDeviceGroupIdCamerasCameraIdDelete(deviceGroupId, cameraId)
      );

      // Reload cameras after removing
      const updatedGroup = await firstValueFrom(
        this.deviceGroupService.deviceGroupsDeviceGroupIdGet(deviceGroupId)
      ) as DeviceGroupDto;
      this.cameras.set(updatedGroup.cameras || []);

      this.snackBar.open(`${cameraName} removed successfully`, 'Close', { duration: 2000 });
    } catch (error) {
      console.error('Error removing camera:', error);
      this.snackBar.open('Failed to remove camera', 'Close', { duration: 3000 });
    } finally {
      this.isProcessing.set(false);
    }
  }

  async createDetectionPattern(request: CreateDetectionSequencePatternRequest): Promise<void> {
    this.isProcessing.set(true);
    try {
      // Debug: Log original request values
      console.log('Pattern creation request received from form:', {
        targetZoneId: request.targetZoneId,
        sourceZoneId: request.sourceZoneId,
        targetZoneType: typeof request.targetZoneId,
        sourceZoneType: typeof request.sourceZoneId
      });

      // Ensure we have proper null values for "outside" selections
      if (request.targetZoneId === '') {
        request.targetZoneId = null;
      }
      if (request.sourceZoneId === '') {
        request.sourceZoneId = null;
      }

      console.log('Processed request before API call:', {
        targetZoneId: request.targetZoneId,
        sourceZoneId: request.sourceZoneId
      });

      const newPattern = await firstValueFrom(
        this.detectionSequencePatternService.detectionSequencePatternsPost(request)
      );

      this.detectionPatterns.update(patterns => [...patterns, newPattern]);
      this.snackBar.open(`Pattern "${request.name}" created successfully`, 'Close', { duration: 2000 });
      this.showCreatePatternForm.set(false);
    } catch (error) {
      console.error('Error creating detection pattern:', error);
      this.snackBar.open('Failed to create detection pattern', 'Close', { duration: 3000 });
    } finally {
      this.isProcessing.set(false);
    }
  }

  async connectCamera(cameraId: string): Promise<void> {
    const connecting = new Set(this.connectingCameras());
    connecting.add(cameraId);
    this.connectingCameras.set(connecting);

    try {
      const camera = this.cameras().find(c => c.id === cameraId);
      const cameraName = camera?.deviceName || 'Camera';

      const openResult = await firstValueFrom(
        this.cameraService.camerasCameraIdConnectPost(cameraId)
      );

      this.snackBar.open(`${openResult}`, 'Close', { duration: 2000 });
    } catch (error) {
      console.error('Error connecting camera:', error);
      this.snackBar.open('Failed to connect camera', 'Close', { duration: 3000 });
    } finally {
      const connecting = new Set(this.connectingCameras());
      connecting.delete(cameraId);
      this.connectingCameras.set(connecting);
    }
  }

  async startCameraLPRListener(cameraId: string): Promise<void> {
    const starting = new Set(this.startingLPRCameras());
    starting.add(cameraId);
    this.startingLPRCameras.set(starting);

    try {
      const camera = this.cameras().find(c => c.id === cameraId);
      const cameraName = camera?.deviceName || 'Camera';

      const startResult = await firstValueFrom(
        this.cameraService.camerasCameraIdStartLprPost(cameraId)
      );

      this.snackBar.open(`${startResult}`, 'Close', { duration: 2000 });
    } catch (error) {
      console.error('Error starting LPR listener:', error);
      this.snackBar.open('Failed to start LPR listener', 'Close', { duration: 3000 });
    } finally {
      const starting = new Set(this.startingLPRCameras());
      starting.delete(cameraId);
      this.startingLPRCameras.set(starting);
    }
  }

  isCameraConnecting(cameraId: string): boolean {
    return this.connectingCameras().has(cameraId);
  }

  isCameraStartingLPR(cameraId: string): boolean {
    return this.startingLPRCameras().has(cameraId);
  }

  navigateToCamera(cameraId: string): void {
    // Switch to cameras tab (index 0)
    this.selectedTabIndex.set(0);

    // Highlight the specific camera
    this.highlightedCameraId.set(cameraId);

    // Clear highlight after 3 seconds
    setTimeout(() => {
      this.highlightedCameraId.set(null);
    }, 3000);
  }

  isHighlightedCamera(cameraId: string): boolean {
    return this.highlightedCameraId() === cameraId;
  }

  async deleteDetectionPoint(pointId: string): Promise<void> {
    this.isProcessing.set(true);
    try {
      const pointToDelete = this.detectionPoints().find(p => p.id === pointId);
      const pointName = pointToDelete?.name || 'Detection Point';

      await firstValueFrom(
        this.detectionPointService.detectionPointsDetectionPointIdDelete(pointId)
      );

      this.detectionPoints.update(points => points.filter(p => p.id !== pointId));
      this.snackBar.open(`${pointName} deleted successfully`, 'Close', { duration: 2000 });
    } catch (error) {
      console.error('Error deleting detection point:', error);
      this.snackBar.open('Failed to delete detection point', 'Close', { duration: 3000 });
    } finally {
      this.isProcessing.set(false);
    }
  }

  async saveDetectionPoint(change: DetectionPointChange): Promise<void> {
    this.isProcessing.set(true);
    try {
      const updated = await firstValueFrom(
        this.detectionPointService.detectionPointsDetectionPointIdPut(change.detectionPointId, change.request)
      ) as DetectionPointDto;

      // The other camera on the same lane takes the same schedule on the server; reload so both cards show it.
      const zoneId = this.zoneId();
      if (zoneId) {
        const pointsReq = await firstValueFrom(
          this.detectionPointService.detectionPointsFilterPost({ targetZoneId: zoneId, pageSize: ZONE_DETECTION_POINTS_PAGE_SIZE } as ListDetectionPointsRequest)
        );
        this.detectionPoints.set((pointsReq as DetectionPointDtoPaginationResult)?.items || []);
      } else {
        this.detectionPoints.update(points => points.map(p => p.id === updated.id ? { ...p, ...updated } : p));
      }
      this.snackBar.open(`${updated.name ?? 'Detection point'} saved`, 'Close', { duration: 2000 });
    } catch (error: any) {
      console.error('Error saving detection point:', error);
      // The backend explains what is wrong (missing entry/exit device group, DST hour, …); show it.
      const detail = error?.error?.detail ?? error?.error?.title ?? 'Failed to save detection point';
      this.snackBar.open(detail, 'Close', { duration: 6000 });
    } finally {
      this.isProcessing.set(false);
    }
  }

  async deleteDetectionPattern(patternId: string): Promise<void> {
    this.isProcessing.set(true);
    try {
      const patternToDelete = this.detectionPatterns().find(p => p.id === patternId);
      const patternName = patternToDelete?.name || 'Detection Pattern';

      await firstValueFrom(
        this.detectionSequencePatternService.detectionSequencePatternsPatternIdDelete(patternId)
      );

      this.detectionPatterns.update(patterns => patterns.filter(p => p.id !== patternId));
      this.snackBar.open(`${patternName} deleted successfully`, 'Close', { duration: 2000 });
    } catch (error) {
      console.error('Error deleting detection pattern:', error);
      this.snackBar.open('Failed to delete detection pattern', 'Close', { duration: 3000 });
    } finally {
      this.isProcessing.set(false);
    }
  }

  editDetectionPattern(patternId: string): void {
    // TODO: Implement edit functionality - could open edit form or navigate to edit page
    const patternToEdit = this.detectionPatterns().find(p => p.id === patternId);
    const patternName = patternToEdit?.name || 'Pattern';

    this.snackBar.open(`Edit functionality for "${patternName}" - Coming soon!`, 'Close', { duration: 3000 });

    // Future implementation could:
    // 1. Open an edit form modal
    // 2. Navigate to edit route
    // 3. Enable inline editing
  }

  startEditingName(): void {
    this.editingGroupName = this.deviceGroup()?.groupName || '';
    this.isEditingName.set(true);
  }

  cancelEditName(): void {
    this.isEditingName.set(false);
    this.editingGroupName = '';
  }

  async onDirectionChange(direction: PointDirection): Promise<void> {
    const current = this.deviceGroup();
    if (!current?.id || direction === this.currentDirection()) return;

    this.isProcessing.set(true);
    try {
      // The whole group is sent back, like the rename does, so nothing else on it changes.
      const request: UpdateDeviceGroupRequest = {
        groupName: current.groupName,
        groupType: current.groupType,
        groupDescription: current.groupDescription,
        direction
      };
      const updated = await firstValueFrom(
        this.deviceGroupService.deviceGroupsDeviceGroupIdPut(current.id, request)
      ) as DeviceGroupDto;
      this.deviceGroup.set(updated);
      this.snackBar.open('Device group direction updated', 'Close', { duration: 2000 });
    } catch (error) {
      console.error('Error updating device group direction:', error);
      this.snackBar.open('Failed to update device group direction', 'Close', { duration: 3000 });
    } finally {
      this.isProcessing.set(false);
    }
  }

  async saveGroupName(): Promise<void> {
    const name = this.editingGroupName.trim();
    if (!name) return;
    const deviceGroupId = this.deviceGroup()?.id;
    if (!deviceGroupId) return;

    this.isProcessing.set(true);
    try {
      const current = this.deviceGroup()!;
      const request: UpdateDeviceGroupRequest = {
        groupName: name,
        groupType: current.groupType,
        groupDescription: current.groupDescription
      };
      const updated = await firstValueFrom(
        this.deviceGroupService.deviceGroupsDeviceGroupIdPut(deviceGroupId, request)
      ) as DeviceGroupDto;
      this.deviceGroup.set(updated);
      this.isEditingName.set(false);
      this.editingGroupName = '';
      this.snackBar.open(`Device group renamed to "${name}"`, 'Close', { duration: 2000 });
    } catch (error) {
      console.error('Error updating device group name:', error);
      this.snackBar.open('Failed to update device group name', 'Close', { duration: 3000 });
    } finally {
      this.isProcessing.set(false);
    }
  }

  onBack(): void {


    if (this.zoneId()) {
      this.router.navigate(['/parking-zone', this.zoneId()]);
      return;
    }
    //  this.router.navigate(['/parking-zone', zoneId]);
    if (this.parkingSpaceId()) {
      this.router.navigate(['/parking-space', this.parkingSpaceId()]);
      return;
    }
    this.router.navigate(['..'], { relativeTo: this.route });
  }

  openCreateCameraForm(): void {
    this.editingCamera.set(null);
    this.showCreateForm.set(true);
  }

  openEditCameraForm(camera: CameraDto): void {
    this.editingCamera.set(camera);
    this.showCreateForm.set(true);
  }

  closeCameraForm(): void {
    this.showCreateForm.set(false);
    this.editingCamera.set(null);
  }

  toggleCreateForm(): void {
    const next = !this.showCreateForm();
    this.showCreateForm.set(next);
    if (!next) {
      this.editingCamera.set(null);
    }
  }

  openCreateBarrierForm(): void {
    this.editingBarrier.set(null);
    this.showCreateBarrierForm.set(true);
  }

  openEditBarrierForm(barrier: BarrierDto): void {
    this.editingBarrier.set(barrier);
    this.showCreateBarrierForm.set(true);
  }

  closeBarrierForm(): void {
    this.showCreateBarrierForm.set(false);
    this.editingBarrier.set(null);
  }

  toggleCreateDetectionPointForm(): void {
    this.showCreateDetectionPointForm.update(v => !v);
  }

  toggleCreateBarrierForm(): void {
    const next = !this.showCreateBarrierForm();
    this.showCreateBarrierForm.set(next);
    if (!next) {
      this.editingBarrier.set(null);
    }
  }

  toggleCreatePatternForm(): void {
    this.showCreatePatternForm.update(v => !v);
  }

  openAssignExistingCamera(): void {
    this.openAssignDialog('camera');
  }

  openAssignExistingBarrier(): void {
    this.openAssignDialog('barrier');
  }

  openAssignExistingDisplay(): void {
    this.openAssignDialog('display');
  }

  private openAssignDialog(deviceType: 'camera' | 'barrier' | 'display'): void {
    const deviceGroupId = this.deviceGroup()?.id;
    const infraId = this.infrastructureId();
    if (!deviceGroupId || !infraId) {
      this.snackBar.open('Zone infrastructure not loaded. Please wait and try again.', 'Close', { duration: 3000 });
      return;
    }

    const data: AssignExistingDeviceDialogData = {
      deviceType,
      currentDeviceGroupId: deviceGroupId,
      infrastructureId: infraId
    };

    const dialogRef = this.dialog.open(AssignExistingDeviceDialogComponent, {
      width: '480px',
      maxWidth: '95vw',
      data
    });

    dialogRef.afterClosed().subscribe(async result => {
      if (!result) return;
      this.isProcessing.set(true);
      try {
        if (result.deviceType === 'camera') {
          await firstValueFrom(
            this.deviceGroupService.deviceGroupsDeviceGroupIdCamerasCameraIdPost(deviceGroupId, result.deviceId)
          );
          // Reload cameras
          const updatedGroup = await firstValueFrom(
            this.deviceGroupService.deviceGroupsDeviceGroupIdGet(deviceGroupId)
          ) as DeviceGroupDto;
          this.cameras.set(updatedGroup.cameras || []);
          this.devices.set(updatedGroup.allDevices || []);
        } else if (result.deviceType === 'display') {
          await firstValueFrom(
            this.displayService.displaysDisplayIdPut(result.deviceId, { deviceGroupId })
          );
          await this.loadDisplaysForGroup(deviceGroupId);
        } else if (result.deviceType === 'barrier') {
          await firstValueFrom(
            this.deviceGroupService.deviceGroupsDeviceGroupIdDevicesDeviceIdPost(deviceGroupId, result.deviceId)
          );
          await this.loadBarrierForGroup(deviceGroupId);
        }
        this.snackBar.open(`${result.deviceName} assigned successfully`, 'Close', { duration: 2000 });
      } catch (error) {
        console.error('Error assigning device:', error);
        this.snackBar.open(`Failed to assign ${result.deviceType}`, 'Close', { duration: 3000 });
      } finally {
        this.isProcessing.set(false);
      }
    });
  }

  async unassignBarrier(barrierId: string): Promise<void> {
    this.isProcessing.set(true);
    const deviceGroupId = this.deviceGroup()?.id;
    if (!deviceGroupId) { this.isProcessing.set(false); return; }
    try {
      await firstValueFrom(
        this.deviceGroupService.deviceGroupsDeviceGroupIdDevicesDeviceIdDelete(deviceGroupId, barrierId)
      );
      this.barrier.set(null);
      this.snackBar.open('Barrier unassigned from group', 'Close', { duration: 2000 });
    } catch (error) {
      console.error('Error unassigning barrier:', error);
      this.snackBar.open('Failed to unassign barrier', 'Close', { duration: 3000 });
    } finally {
      this.isProcessing.set(false);
    }
  }

  async unassignDisplay(displayId: string): Promise<void> {
    this.isProcessing.set(true);
    const deviceGroupId = this.deviceGroup()?.id;
    if (!deviceGroupId) { this.isProcessing.set(false); return; }
    try {
      await firstValueFrom(
        this.displayService.displaysDisplayIdPut(displayId, { deviceGroupId: null })
      );
      this.displays.update(list => list.filter(d => d.id !== displayId));
      this.snackBar.open('Display unassigned from group', 'Close', { duration: 2000 });
    } catch (error) {
      console.error('Error unassigning display:', error);
      this.snackBar.open('Failed to unassign display', 'Close', { duration: 3000 });
    } finally {
      this.isProcessing.set(false);
    }
  }
}



